/*
 * Copyright (C) 2023 - present by Potentially
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { MatDialog as MatNonLegacyDialog } from '@angular/material/dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import { USER_GROUPS_DATA_SERVICE, UserGroupsDataService } from '@app/app/page-modules/admin/services/groups/groups-data.service';
import { ROLES_DATA_SERVICE, RolesDataService } from '@app/app/page-modules/admin/services/roles/roles-data.service';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { CloseReviewsModalComponent } from '@app/app/page-modules/reviews/components/view/submissions-controls/close-reviews-modal/close-reviews-modal.component';
import { ReviewAction, ReviewAdminRequest } from '@app/app/page-modules/reviews/models';
import { REVIEWS_DATA_SERVICE, ReviewsDataService } from '@app/app/page-modules/reviews/services/data.service';
import { DialogService } from '@app/app/shared/helpers/dialog/dialog.service';
import { v3DialogConfig } from '@app/app/shared/helpers/dialog/v3-dialog-config';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { Organization, Playlist, PlaylistPublicationSettings, Resource, UserSubmissionMode } from '@app/app/shared/models';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { displayableGroupTypeLabel } from '@app/app/shared/models/admin/group/user-groups.model';
import { CheckedMembersData } from '@app/app/shared/models/admin/members.model';
import { LtiMyHubService } from '@app/app/shared/services/lti-my-hub.service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import { LoadableState, ObservableResult } from '@app/app/shared/store';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import {
  LucideCalendarCheck,
  LucideClipboardCheck,
  LucideMail,
  LucideQrCode,
  LucideShield,
  LucideUpload,
  LucideUsers,
} from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable, Subscription, combineLatest, map, of, takeUntil } from 'rxjs';
import { PlaylistResourceEditorPublicationState } from 'src/app/page-modules/playlist/store/create/publication/playlist-publication.state';
import { PlaylistViewState } from '../../../../../playlist/store/view/playlist-view.state';
import { RedirectHelper } from '../../../../../resource/store/editor/content/helpers/redirect.helper';
import { CheckInDialogComponent } from '../../admin-member-control-panel/check-in-dialog/check-in-dialog.component';
import { AdminMembersAction, AdminMembersActionType, MembersListColumns } from '../../model/members.model';
import { V3AlumniCsvUploadDialogComponent } from '../../v3-member-dialogs/v3-alumni-csv-upload-dialog/v3-alumni-csv-upload-dialog.component';
import {
  V3EditMembershipDialogComponent,
  V3EditMembershipDialogData,
} from '../../v3-member-dialogs/v3-edit-membership-dialog/v3-edit-membership-dialog.component';
import {
  V3UsersCsvUploadDialogComponent,
  V3UsersCsvUploadResult,
} from '../../v3-member-dialogs/v3-users-csv-upload-dialog/v3-users-csv-upload-dialog.component';

@Component({
  selector: 'ptl-v3-admin-action-buttons',
  templateUrl: './v3-admin-action-buttons.component.html',
  styleUrls: ['./v3-admin-action-buttons.component.scss'],
})
export class V3AdminActionButtonsComponent implements OnInit, OnDestroy {
  @Input() columns: MembersListColumns[];
  @Input() isAdminPage: boolean;
  @Input() isPlaylistPage: boolean;
  @Input() isCardPage: boolean;
  @Input() isAssessment: boolean;
  @Input() isEvent: boolean;
  @Input() checkedMembers: CheckedMembersData[] = [];
  @Input() totalMembersCount: number;
  @Input() allMembersCount: number;
  @Input() booleanSearchRequest: MembersBooleanSearchRequest;
  @Input() playlistUid: string;
  @Input() cardUid: string;
  @Input() exportEnabled: boolean;
  @Input() exportName: string;
  @Input() isWaitingListEnabled: boolean;
  @Input() eventFinished: boolean;
  @Input() isAllSelected: boolean;
  @Input() isWaitingListOperationDisabled: boolean;
  @Input() courseUid?: string;

  @Output() reviewersChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() csvFileUploadFinished: EventEmitter<number> = new EventEmitter<number>();
  @Output() triggerUserImport: EventEmitter<{ file: File; headers: string[] }> = new EventEmitter<{ file: File; headers: string[] }>();

  @Output() refreshList = new EventEmitter<number>();
  @Output() memberActionTriggered = new EventEmitter<AdminMembersAction>();

  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @Select(UserAuthState.canManageUserImportPrivileges)
  canManageUserImportPrivileges$: Observable<boolean>;

  @Select(ResourceAdminState.resource)
  private resource$: Observable<Resource>;

  @Select(UserAuthState.userHasSuperAdminRole)
  private isUserSuperAdmin$: Observable<boolean>;

  @Select(UserAuthState.userHasAdminRole)
  private isUserAdmin$: Observable<boolean>;

  @Select(PlaylistResourceEditorPublicationState.publicationSettings)
  private playlistPublicationSettings$: Observable<PlaylistPublicationSettings>;

  @Select(PlaylistViewState.playlistState)
  private playlist$: Observable<LoadableState<Playlist>>;

  reviewActions = ReviewAction;

  protected readonly LucideCalendarCheck = LucideCalendarCheck;
  protected readonly LucideClipboardCheck = LucideClipboardCheck;
  protected readonly LucideMail = LucideMail;
  protected readonly LucideQrCode = LucideQrCode;
  protected readonly LucideShield = LucideShield;
  protected readonly LucideUpload = LucideUpload;
  protected readonly LucideUsers = LucideUsers;

  advancedExportAvailable = false;
  importDisabledTooltipText: string;

  cardPublished = false;
  playlistPublished = false;
  limitedTickets = false;
  remainingTickets: number;
  ticketsQuantity: number;
  canManageReviews: boolean;
  isImportDisabled = false;

  private subscriptionEnd$ = new EventEmitter<void>();
  private sendMessageDialogOpening = false;
  private resource: Resource;
  private contentSubscription: Subscription;
  private organizationDataSubscription: Subscription;
  private orgDomain: string;
  private playlistSubmissionMode: UserSubmissionMode;

  private readonly ltiMyHubService = inject(LtiMyHubService);

  constructor(
    private activatedRoute: ActivatedRoute,
    private translocoService: TranslocoService,
    private dialog: MatDialog,
    private nonLegacyDialog: MatNonLegacyDialog,
    private dialogService: DialogService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(REVIEWS_DATA_SERVICE) private reviewsDataService: ReviewsDataService,
    @Inject(TOAST_NOTIFICATION_SERVICE) private toastService: ToastService,
    @Inject(ROLES_DATA_SERVICE) private rolesDataService: RolesDataService,
    @Inject(USER_GROUPS_DATA_SERVICE) private userGroupsService: UserGroupsDataService,
  ) {}

  /** Live: the LMS mode, which is the full app here, turns on once the organisation has loaded. */
  get isIframeMode(): boolean {
    return this.ltiMyHubService.embedWithoutNav();
  }

  ngOnInit(): void {
    combineLatest([this.isUserAdmin$, this.isUserSuperAdmin$])
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe((data) => {
        this.advancedExportAvailable = this.checkIfUserCanViewExports(data[0], data[1]);
      });

    this.playlist$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((playlist) => {
      if (playlist && playlist.data) {
        this.canManageReviews = playlist?.data?.userPlaylistSubmissionSummary?.userReviewSummary?.reviewType !== 'AUTOMATIC';
        this.playlistSubmissionMode = playlist?.data?.userPlaylistSubmissionSummary?.submissionMode;
      }
    });

    this.playlistPublicationSettings$.subscribe((publicationSettings) => {
      if (publicationSettings) {
        this.playlistPublished = publicationSettings.published;
        if (this.playlistPublished) {
          this.importDisabledTooltipText = undefined;
        } else {
          this.importDisabledTooltipText = this.translocoService.translate(
            'translations.membersNew.controlPanel.import.importDisabledTooltipTextPlaylist',
          );
        }
        this.isImportDisabled = !this.playlistPublished;
        this.cdr.detectChanges();
      }
    });
    if (this.isCardPage) {
      this.contentSubscription = this.resource$.subscribe((resource) => {
        if (resource) {
          this.resource = resource;
          this.cardPublished = resource.status === 'PUBLISHED';
          if (this.cardPublished) {
            this.importDisabledTooltipText = undefined;
          } else {
            this.importDisabledTooltipText = this.translocoService.translate(
              'translations.membersNew.controlPanel.import.importDisabledTooltipText',
            );
          }
          if (resource.tickets?.availability === 'LIMITED') {
            this.limitedTickets = true;
            this.remainingTickets = resource.tickets.remainingTickets;
            this.ticketsQuantity = resource.tickets.quantity;
          }
          this.isImportDisabled = !this.cardPublished;
          this.cdr.detectChanges();
        }
      });
    }
    this.organizationDataSubscription = this.organizationData$.subscribe((data) => {
      this.orgDomain = data.domain;
    });
  }

  ngOnDestroy(): void {
    this.contentSubscription?.unsubscribe();
    this.organizationDataSubscription?.unsubscribe();
    this.subscriptionEnd$.emit();
  }

  async reviewAction(operationType: string): Promise<void> {
    if (!this.getCheckedReviewUids()) {
      return;
    }

    let response: ObservableResult<void>;

    if (operationType !== ReviewAction.CLOSE) {
      const isConfirmed = await this.showConfirmDialog(operationType);
      if (!isConfirmed) {
        return;
      }

      if (this.isAllSelected) {
        response = this.doAllItemsOperation(operationType);
      } else {
        response = this.doSelectedItemsOperation(operationType);
      }
      response.subscribe(({ isSuccess, value, error }) => {
        if (isSuccess) {
          this.reviewersChanged.emit();
        } else {
          this.toastService.showFail(error);
        }
      });
    } else {
      this.handleCloseAction(operationType);
    }
  }

  doAllItemsOperation(operationType: string) {
    if (operationType === ReviewAction.REOPEN) {
      return this.reviewsDataService.reOpenReview({ reviewUids: this.getCheckedReviewUids() });
    }

    return of();
  }

  doSelectedItemsOperation(operationType: string) {
    if (operationType === ReviewAction.REOPEN) {
      return this.reviewsDataService.reOpenReview({ reviewUids: this.getCheckedReviewUids() });
    }

    return of();
  }

  showConfirmDialog(key) {
    const keyLowerCase = key.toLowerCase();
    const upperCaseFirstLetter = keyLowerCase.charAt(0).toUpperCase() + keyLowerCase.slice(1);
    const message = this.translocoService.translate('translations.dialog.title.reviewsBulk' + upperCaseFirstLetter);
    const config = {};
    return this.dialogService.showConfirmDialog(message, this.translocoService, config);
  }

  private getCheckedMembersIds(): string[] {
    return this.checkedMembers.map((item) => item._id);
  }

  private getCheckedReviewUids(): string[] {
    return this.checkedMembers.filter((item) => !!item.reviewUid).map((item) => item.reviewUid);
  }

  private handleCloseAction(operationType: string): void {
    const dialogRef = this.dialog.open(CloseReviewsModalComponent, {
      width: '37.5rem',
      disableClose: true,
      maxHeight: '80vh',
      position: {
        top: '37vh',
      },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: ['ptl-mat-dialog'],
      backdropClass: 'dialog-backdrop',
      data: {
        playlistSubmissionMode: this.playlistSubmissionMode,
        playlistId: this.playlistUid,
      },
    });

    const dialogSubscription = dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (data.confirmed) {
          if (data.reviewCloseReasonSettings) {
            data.reviewCloseReasonSettings.reviewUids = this.getCheckedReviewUids();
          }
          const closeResponse = this.handleCloseWithAcceptance(data.reviewCloseReasonSettings, data.withAcceptance);
          closeResponse.subscribe((value) => {
            if (value) {
              this.reviewersChanged.emit();
            }
          });
        }
      }
      dialogSubscription.unsubscribe();
    });
  }

  openEditRoleDialog(): void {
    this.openMembershipDialog(this.rolesMembershipConfig());
  }

  openEditGroupDialog(): void {
    this.openMembershipDialog(this.groupsMembershipConfig());
  }

  invokeEventAction(actionType: AdminMembersActionType): void {
    if (!this.checkedMembers.length) {
      return;
    }

    if (this.isEvent) {
      this.memberActionTriggered.emit({
        type: actionType,
        memberIds: this.getCheckedMembersIds(),
        allMembersRefresh: false,
      });
    }
  }

  openAlumniUploadCsv(): void {
    this.nonLegacyDialog
      .open<V3AlumniCsvUploadDialogComponent, unknown, number | undefined>(V3AlumniCsvUploadDialogComponent, v3DialogConfig())
      .afterClosed()
      .subscribe((processed) => {
        if (typeof processed === 'number') {
          this.csvFileUploadFinished.emit(processed);
        }
      });
  }

  openUsersUploadCsv(): void {
    this.nonLegacyDialog
      .open<V3UsersCsvUploadDialogComponent, unknown, V3UsersCsvUploadResult | undefined>(V3UsersCsvUploadDialogComponent, v3DialogConfig())
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.triggerUserImport.emit(result);
        }
      });
  }

  private openMembershipDialog(config: Omit<V3EditMembershipDialogData, 'checkedMemberIds'>): void {
    if (!this.checkedMembers.length) {
      return;
    }

    this.ngZone.run(() => {
      this.nonLegacyDialog
        .open<V3EditMembershipDialogComponent, V3EditMembershipDialogData, boolean>(
          V3EditMembershipDialogComponent,
          v3DialogConfig({ data: { ...config, checkedMemberIds: this.getCheckedMembersIds() } }),
        )
        .afterClosed()
        .subscribe((changed) => {
          if (changed) {
            this.reviewersChanged.emit();
          }
        });
    });
  }

  private rolesMembershipConfig(): Omit<V3EditMembershipDialogData, 'checkedMemberIds'> {
    return {
      titleKey: 'translations.membersNew.actionBar.editRoleDialog.title',
      descriptionKey: 'translations.membersNew.actionBar.editRoleDialog.description',
      searchLabelKey: 'translations.global.search.label.role',
      searchPlaceholderKey: 'translations.rolesV3.searchPlaceholder',
      emptyKey: 'translations.rolesV3.empty.title',
      addButtonKey: 'translations.membersNew.actionBar.editRoleDialog.addRolesBtnText',
      removeButtonKey: 'translations.membersNew.actionBar.editRoleDialog.removeRoles',
      addSuccessKey: 'translations.successes.successAssignRolesToUsers',
      removeSuccessKey: 'translations.successes.successRemoveRolesFromUsers',
      loadErrorKey: 'translations.errors.errorGetRoles',
      search: (page, pageSize, term) =>
        this.rolesDataService.loadRolesByPageWithSearch(term, page, pageSize).pipe(
          map(({ isSuccess, value }) =>
            isSuccess && value
              ? {
                  items: (value.content ?? []).map((role) => ({ id: role._id, label: LanguageCodeHelper.getPlainValue(role.name) })),
                  total: value.totalNumberOfElement ?? 0,
                }
              : null,
          ),
        ),
      add: (roleUids, memberIds) => this.rolesDataService.assignMultipleRolesToMultipleUsers(roleUids, memberIds),
      remove: (roleUids, memberIds) => this.rolesDataService.removeMultipleRolesFromMultipleUsers(roleUids, memberIds),
    };
  }

  private groupsMembershipConfig(): Omit<V3EditMembershipDialogData, 'checkedMemberIds'> {
    return {
      titleKey: 'translations.membersNew.actionBar.editGroupDialog.title',
      descriptionKey: 'translations.membersNew.actionBar.editGroupDialog.description',
      searchLabelKey: 'translations.global.search.label.group',
      searchPlaceholderKey: 'translations.global.search.label.group',
      emptyKey: 'translations.global.search.label.noResultsFound',
      addButtonKey: 'translations.membersNew.actionBar.editGroupDialog.addGroupsBtnText',
      removeButtonKey: 'translations.membersNew.actionBar.editGroupDialog.removeGroups',
      addSuccessKey: 'translations.successes.successAssignGroupsToUsers',
      removeSuccessKey: 'translations.successes.successRemoveGroupsFromUsers',
      loadErrorKey: 'translations.errors.errorGetUserGroups',
      search: (page, pageSize, term) =>
        this.userGroupsService.searchUserGroups(page, pageSize, term).pipe(
          map(({ isSuccess, value }) =>
            isSuccess && value
              ? {
                  items: (value.content ?? []).map((group) => ({
                    id: group._id,
                    label: group.title,
                    sublabel: displayableGroupTypeLabel(group.type),
                  })),
                  total: value.totalNumberOfElement ?? 0,
                }
              : null,
          ),
        ),
      add: (groupUids, memberIds) => this.userGroupsService.assignMultipleGroupsToMultipleUsers(groupUids, memberIds),
      remove: (groupUids, memberIds) => this.userGroupsService.removeMultipleGroupsFromMultipleUsers(groupUids, memberIds),
    };
  }

  private handleCloseWithAcceptance(closeReviewData: ReviewAdminRequest, withAcceptance: boolean): ObservableResult<void> {
    return this.reviewsDataService.closeReview(closeReviewData, withAcceptance);
  }

  private checkIfUserCanViewExports(isAdmin: boolean, isSuperAdmin: boolean): boolean {
    return isAdmin || isSuperAdmin;
  }

  openCheckInDialog(): void {
    if (!this.cardPublished) throw new Error('Event is not published');
    const start = this.resource.tickets ? this.resource.tickets.time.start : null;
    const end = this.resource.tickets ? this.resource.tickets.time.end : null;
    this.dialog.open(CheckInDialogComponent, {
      width: '90vw',
      minWidth: '15.625rem',
      maxWidth: '46.875rem',
      maxHeight: '80vh',
      restoreFocus: true,
      position: {
        top: '10vh',
      },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-mat-dialog',
      backdropClass: 'dialog-backdrop',
      data: {
        eventTitle: this.resource.content.headline,
        eventUrl: this.getEventUrl(),
        eventStartDate: start ? new Date(start.split(',')[0]) : null,
        eventEndDate: end ? new Date(end.split(',')[0]) : null,
      },
    });
  }

  private getEventUrl(): string {
    const publisherUri = this.activatedRoute.snapshot.paramMap.get('publisherUri');
    const packageUri = this.activatedRoute.snapshot.paramMap.get('packageUri');
    const playlistUri = this.activatedRoute.snapshot.paramMap.get('playlistUri');
    const resourceUri = this.activatedRoute.snapshot.paramMap.get('resourceUri');
    const params = {
      publisherUri: publisherUri,
      packageUri: packageUri,
      playlistUri: playlistUri,
      resourceUri: resourceUri,
      pageNumberUri: 'page/1',
    };
    if (this.resource.participationCode) {
      params['queryUriParam'] = `?code=${this.resource.participationCode}`;
    }
    const eventUri = RedirectHelper.getRedirectUrl(this.activatedRoute, params, 'EVENT');
    return `https://${this.orgDomain}${eventUri}`;
  }

  // Imported on demand: the dialog pulls in TiptapModule, which imports SharedModule — the module that
  // declares this component. A static import closes that cycle and blanks the app at startup.
  async openSendMessageDialog(): Promise<void> {
    if (this.sendMessageDialogOpening) {
      return;
    }
    this.sendMessageDialogOpening = true;
    const { V3SendMessageDialogComponent } = await import(
      '../../v3-member-dialogs/v3-send-message-dialog/v3-send-message-dialog.component'
    ).finally(() => (this.sendMessageDialogOpening = false));

    this.ngZone.run(() => {
      this.nonLegacyDialog.open(
        V3SendMessageDialogComponent,
        v3DialogConfig({
          width: '46.875rem',
          data: {
            checkedMemberIds: this.getCheckedMembersIds(),
            query: this.booleanSearchRequest.query,
            allMembers: this.totalMembersCount === this.allMembersCount,
            filteredCount: this.totalMembersCount,
            playlistUid: this.playlistUid,
            cardUid: this.cardUid,
          },
        }),
      );
    });
  }
}
